import React, { useEffect, useMemo, useRef, useState } from "react";
import { FiCheck, FiX } from "react-icons/fi";
import axios from "../../../api/axiosInstance";
import { usePmsTheme } from "../../../Components/PmsThemeContext";
import { readStoredUser } from "../../../utils/sessionUser";
import { hasPurchasingSettingsPermission } from "../../../utils/purchasingSettingsPermissions";
import { InventoryWorkspaceDialog } from "../InventoryFilterPopup";
import { getAccessRuleEditorMessages } from "./accessRuleEditorMessages";
import { getUserAccessMessages } from "../userAccessMessages";
import { getLocationName } from "../../utils/locationNames";
import { loadUserAccessPolicy } from "../../services/userAccessPolicy";
import ItemAccessTreeEditor from "./ItemAccessTreeEditor";
import { buildAccessTree, delegationLimits, describePolicy, LEVELS, serializeRule } from "./itemAccessTree";
import "./AddUserAccessRulePopup.css";

const CAPABILITIES = ["receive", "count", "dispatch", "transfer"];
const positiveId = (value) => Number.isInteger(Number(value)) && Number(value) > 0;
const ruleSignature = rows => JSON.stringify(rows.map(rule => JSON.stringify(serializeRule(rule))).sort());
function unsupportedLocations(locations, catalog) {
  let unsupported = false;
  const ids = locations.map((row) => Number(row.location_id));
  if (new Set(ids).size !== ids.length) unsupported = true;
  locations.forEach((row) => {
    const available = catalog.locations.find((entry) => Number(entry.id) === Number(row.location_id));
    if (!positiveId(row.location_id) || !available || available.is_active === false || row.is_active !== true ||
      typeof row.is_default !== "boolean" || CAPABILITIES.some((capability) => typeof row[`can_${capability}`] !== "boolean")) unsupported = true;
  });
  if (locations.filter((row) => row.is_default).length > 1) unsupported = true;
  return unsupported;
}

// Always load both saved policies before editing. Initial rule arrays are display
// hints from the list, not a safe substitute for the complete server policy.
export default function AddUserAccessRulePopup({ isOpen, onClose, onAdded, initialUserId = null }) {
  const { languageId } = usePmsTheme();
  const words = getAccessRuleEditorMessages(languageId);
  const accessWords = getUserAccessMessages(languageId);
  const [catalog, setCatalog] = useState(null);
  const [selectedUser, setSelectedUser] = useState("");
  const [locations, setLocations] = useState([]);
  const [rules, setRules] = useState([]);
  const [savedSignature, setSavedSignature] = useState(null);
  const [loadedUser, setLoadedUser] = useState("");
  const [loadingCatalog, setLoadingCatalog] = useState(false);
  const [loadingPolicy, setLoadingPolicy] = useState(false);
  const [error, setError] = useState(null);
  const [unsupported, setUnsupported] = useState(false);
  const [migrationRequired, setMigrationRequired] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const savingRef = useRef(false);
  const currentUser = readStoredUser() || {};
  const canView = hasPurchasingSettingsPermission(currentUser, "access", "view");
  const canUpdate = canView && hasPurchasingSettingsPermission(currentUser, "access", "update");
  const businessUnitId = currentUser.active_business_unit_id;

  useEffect(() => {
    if (!isOpen || !canView) return undefined;
    let cancelled = false;
    setCatalog(null); setLoadedUser(""); setSelectedUser(initialUserId ? String(initialUserId) : "");
    setLocations([]); setRules([]); setDirty(false);
    setError(null); setUnsupported(false); setPendingAction(null); setSaved(false);
    setLoadingCatalog(true); setLoadingPolicy(false);
    const load = async () => {
      try {
        if (!businessUnitId) { setError({ key: "noBusinessUnit" }); return; }
        const { data } = await axios.get("/api/purchasing/user-item-access-rules/context", { params: { business_unit_id: businessUnitId } });
        if (!["users", ...LEVELS.slice(0, 4).map((level) => level.group), "locations"].every((key) => Array.isArray(data?.[key]))) {
          throw new Error("Invalid access configuration context");
        }
        if (cancelled) return;
        setCatalog(data);
      } catch (requestError) {
        if (!cancelled) setError({ key: "catalogError", detail: requestError.response?.data?.error });
      } finally { if (!cancelled) setLoadingCatalog(false); }
    };
    load();
    return () => { cancelled = true; };
  }, [isOpen, initialUserId, businessUnitId, canView]);

  useEffect(() => {
    if (!isOpen || !canView || !catalog || !selectedUser) return undefined;
    let cancelled = false;
    setLoadedUser(""); setLoadingPolicy(true); setError(null); setUnsupported(false);
    setLocations([]); setRules([]); setDirty(false); setSaved(false);
    const load = async () => {
      try {
        const { rules, locations: rows, migrationRequired } = await loadUserAccessPolicy(selectedUser);
        if (cancelled) return;
        setMigrationRequired(migrationRequired);
        setUnsupported(unsupportedLocations(rows, catalog)); setRules(rules); setSavedSignature(ruleSignature(rules)); setLocations(rows.map((row) => ({ ...row })));
        setLoadedUser(selectedUser);
      } catch (requestError) {
        if (!cancelled) setError({ accessKey: requestError.messageKey || "loadError" });
      } finally { if (!cancelled) setLoadingPolicy(false); }
    };
    load();
    return () => { cancelled = true; };
  }, [catalog, isOpen, selectedUser, canView]);

  const tree = useMemo(() => buildAccessTree(catalog, rules), [catalog, rules]);
  const ceiling = useMemo(() => buildAccessTree(catalog, Array.isArray(catalog?.delegation_rules) ? catalog.delegation_rules : []), [catalog]);
  const limits = useMemo(() => delegationLimits(tree, ceiling), [tree, ceiling]);
  const missingCeiling = !Array.isArray(catalog?.delegation_rules) || ceiling.unsupported;
  const signature = useMemo(() => ruleSignature(rules), [rules]);
  const exceedsCeiling = limits.outside && signature !== savedSignature;
  const summary = useMemo(() => describePolicy(rules, tree, words), [rules, tree, words]);
  const validUser = Boolean(catalog?.users.some((user) => String(user.id) === selectedUser));
  const ready = Boolean(catalog && selectedUser && loadedUser === selectedUser && validUser);
  const disabled = !ready || unsupported || tree.unsupported || missingCeiling || migrationRequired || !canUpdate || saving || saved;
  const locationDefaultValid = !locations.length || locations.filter((row) => row.is_default).length === 1;
  const locationName = (id) => getLocationName({ name: catalog?.locations.find((entry) => Number(entry.id) === Number(id))?.name || locations.find((entry) => Number(entry.location_id) === Number(id))?.location?.name }, languageId);

  if (!isOpen || !canView) return null;

  const requestClose = () => {
    if (savingRef.current) return;
    if (dirty && !saved) setPendingAction({ kind: "close" }); else onClose?.();
  };
  const selectUser = (value) => {
    if (savingRef.current) return;
    if (dirty) setPendingAction({ kind: "user", value }); else setSelectedUser(value);
  };
  const discard = () => {
    const action = pendingAction;
    setPendingAction(null); setDirty(false);
    if (action.kind === "close") onClose?.(); else setSelectedUser(action.value);
  };
  const updateRules = (next) => {
    if (disabled) return;
    setRules(next); setDirty(true);
  };
  const addLocation = (value) => {
    const id = Number(value);
    if (!positiveId(id) || locations.some((row) => Number(row.location_id) === id)) return;
    setLocations((current) => [...current, { location_id: id, is_default: current.length === 0,
      can_receive: true, can_count: true, can_dispatch: true, can_transfer: true, is_active: true, isNew: true }]);
    setDirty(true);
  };
  const save = async () => {
    if (savingRef.current || disabled || !dirty || exceedsCeiling) return;
    if (!locationDefaultValid) { setError({ key: "selectDefault" }); return; }
    const policy = { policy_version: 2, rules: rules.map(serializeRule) };
    const location_access = locations.map((row) => ({
      location_id: Number(row.location_id), is_default: row.is_default, is_active: row.is_active,
      ...Object.fromEntries(CAPABILITIES.map((capability) => [`can_${capability}`, row[`can_${capability}`]])),
    }));
    savingRef.current = true; setSaving(true); setError(null);
    try {
      await axios.put(`/api/purchasing/user-item-access-rules/user/${selectedUser}?business_unit_id=${businessUnitId}`, { ...policy, location_access });
      setSaved(true); setDirty(false);
      try { await onAdded?.(); onClose?.(); }
      catch { setError({ key: "refreshError" }); }
    } catch (requestError) {
      const code = requestError.response?.data?.code;
      if (code === "ITEM_ACCESS_DELEGATION_DENIED") setError({ key: "grantDenied" });
      else if (code === "ITEM_ACCESS_CATALOG_BUSY") setError({ accessKey: "catalogBusy" });
      else setError({ key: "saveError", detail: requestError.response?.data?.error });
    }
    finally { savingRef.current = false; setSaving(false); }
  };

  return <InventoryWorkspaceDialog title={words.title} onClose={requestClose} actions={pendingAction ? <>
    <span className="pu-access-footerNote">{pendingAction.kind === "close" ? words.dirtyClose : words.dirtyUser}</span>
    <button type="button" onClick={() => setPendingAction(null)}>{words.keep}</button>
    <button type="button" className="pu-access-discard" onClick={discard}>{words.discard}</button>
  </> : <>
    <span className="pu-access-footerNote" role="status">{dirty ? words.changed : words.noChanges}</span>
    <button type="button" disabled={saving} onClick={requestClose}>{words.cancel}</button>
    {canUpdate && <button type="button" className="inventory-workspace-dialog__primary" disabled={disabled || !dirty || exceedsCeiling || !locationDefaultValid} onClick={save}>{saving ? words.saving : words.save}</button>}
  </>}>
    <div className="pu-access-editor" aria-busy={loadingCatalog || loadingPolicy || saving}>
      {error && <div className="pu-access-alert pu-access-alert--error" role="alert">{error.accessKey ? accessWords[error.accessKey] : words[error.key]}{error.detail && <small>{error.detail}</small>}</div>}
      {ready && migrationRequired && <div className="pu-access-alert" role="status">{accessWords.migrationRequired}</div>}
      {ready && canUpdate && !migrationRequired && missingCeiling && <p className="pu-access-alert" role="alert">{words.missingCeiling}</p>}
      {pendingAction && <div className="pu-access-alert" role="alert">{pendingAction.kind === "close" ? words.dirtyClose : words.dirtyUser}</div>}
      {!canUpdate && <div className="pu-access-alert">{words.readOnly}</div>}
      {(loadingCatalog || loadingPolicy) && <p className="pu-access-loading" role="status">{loadingCatalog ? words.loadingCatalog : words.loadingPolicy}</p>}
      <section className="pu-access-section">
        <div className="pu-access-user">
          <h3><span>1</span>{words.user}</h3>
          <label><span className="pu-access-srOnly">{words.user}</span><select value={selectedUser} disabled={loadingCatalog || loadingPolicy || saving || Boolean(initialUserId) || saved}
            onChange={(event) => selectUser(event.target.value)}>
            <option value="">{words.chooseUser}</option>
            {selectedUser && !validUser && <option value={selectedUser}>#{selectedUser}</option>}
            {catalog?.users.map((user) => <option key={user.id} value={user.id}>{user.name}{user.email ? ` · ${user.email}` : ""}</option>)}
          </select></label>
        </div>
        <p>{words.userHint}</p>
        {!loadingCatalog && selectedUser && catalog && !validUser && <p className="pu-access-errorText" role="alert">{words.invalidUser}</p>}
      </section>

      {!selectedUser && !loadingCatalog && <p className="pu-access-loading">{words.selectUserFirst}</p>}
      {selectedUser && <section className="pu-access-section">
        <h3><span>2</span>{words.items}</h3><p>{words.itemHint}</p>
        {(unsupported || tree.unsupported) && <div className="pu-access-alert pu-access-alert--error" role="alert">{words.unsupported}</div>}
        {ready && !missingCeiling && limits.outside && <p className="pu-access-alert" role="note">{words.outsideScope}</p>}
        <ItemAccessTreeEditor key={businessUnitId + "-" + selectedUser} tree={tree} ceiling={ceiling} limits={limits} rules={rules} summary={summary} words={words} businessUnitId={businessUnitId}
          ready={ready} disabled={disabled} onChange={updateRules} />
        {(unsupported || tree.unsupported) && <details className="pu-access-original"><summary>{words.rawRules}</summary>
          <ul>{rules.map((rule, index) => <li key={index}>{words.ruleLabels[rule.rule_type] || rule.rule_type} · {LEVELS
            .filter(({ kind }) => rule[kind + "_id"] != null).map(({ kind, group }) => words[group] + " · " + (rule[kind]?.name || "#" + rule[kind + "_id"])).join(" / ") || words.allItems}</li>)}</ul>
        </details>}
      </section>}

      {selectedUser && <section className="pu-access-section">
        <div className="pu-access-locationHeading"><h3><span>3</span>{words.locations}</h3>{canUpdate && <label><span className="pu-access-srOnly">{words.addLocation}</span>
          <select value="" disabled={disabled} onChange={(event) => addLocation(event.target.value)}>
            <option value="">{words.addLocation}</option>
            {catalog?.locations.filter((location) => location.is_active !== false && !locations.some((row) => Number(row.location_id) === Number(location.id))).map((location) => <option key={location.id} value={location.id}>{location.name}</option>)}
          </select></label>}</div>
        <p>{words.locationHint}</p>
        {!locations.length ? <div className="pu-access-fallback">{words.fallback}</div> : <div className="pu-access-tableWrap">
          <table className="pu-access-locations"><thead><tr><th>{words.location}</th><th>{words.default}</th>{CAPABILITIES.map((key) => <th key={key}>{words[key]}</th>)}<th><span className="pu-access-srOnly">{words.remove}</span></th></tr></thead>
            <tbody>{locations.map((row, index) => <tr key={`${row.location_id}-${index}`}>
              <th scope="row">{locationName(row.location_id)}{row.isNew && <small>{words.newLocation}</small>}</th>
              <td><input type="radio" name="access-default-location" checked={row.is_default === true} disabled={disabled} aria-label={`${words.default}: ${locationName(row.location_id)}`}
                onChange={() => { setLocations((current) => current.map((entry) => ({ ...entry, is_default: entry.location_id === row.location_id }))); setDirty(true); }} /></td>
              {CAPABILITIES.map((capability) => <td key={capability} title={words.savedFlag}>
                <span className={row[`can_${capability}`] ? "pu-access-flag" : "pu-access-muted"} aria-label={`${words[capability]}: ${row[`can_${capability}`] ? words.enabled : words.notEnabled}`}>{row[`can_${capability}`] ? <FiCheck aria-hidden="true" /> : "—"}</span>
              </td>)}
              <td>{canUpdate && <button type="button" className="pu-access-remove" disabled={disabled} aria-label={`${words.remove}: ${locationName(row.location_id)}`}
                onClick={() => { setLocations((current) => current.filter((entry) => entry !== row)); setDirty(true); }}><FiX aria-hidden="true" /></button>}</td>
            </tr>)}</tbody></table>
        </div>}
        <p className="pu-access-small">{words.capabilitiesHint}</p>
        {locations.some((row) => CAPABILITIES.some((capability) => row[`can_${capability}`] === false)) &&
          <div className="pu-access-alert" role="note">{words.capabilitiesWarning}</div>}
        {!locationDefaultValid && <p className="pu-access-errorText">{words.selectDefault}</p>}
      </section>}

      {ready && !unsupported && !tree.unsupported && <section className="pu-access-review" aria-label={words.preview}>
        <strong>{words.preview}</strong><div><span>{summary.preview}</span><span>{locations.length ? words.locationCount(locations.length) : words.allLocations}</span></div><p>{words.saveHint}</p>
      </section>}
    </div>
  </InventoryWorkspaceDialog>;
}
